/**
 * Reliable Counter Animation Script
 * Replaces jquery.countup.js with a more robust implementation
 * Uses Intersection Observer API for reliable scroll detection
 */

class CounterAnimation {
  constructor(element, options = {}) {
    this.element = element;
    this.startValue = parseFloat(options.startValue || 0);

    // Parse the target value from the element's text content
    const targetText = this.element.textContent.trim();
    this.suffix = this.extractSuffix(targetText);
    this.targetValue = this.parseNumber(targetText);

    // Configuration
    this.duration = options.duration || 2000; // 2 seconds default
    this.easingFn = options.easing || this.easeOutExpo;
    this.decimalPlaces = this.getDecimalPlaces(targetText);
    this.separator = options.separator || '.'; // German decimal separator
    this.thousandsSeparator = options.thousandsSeparator || ''; // No thousands separator by default

    // State
    this.hasAnimated = false;
    this.currentValue = this.startValue;
    this.animationFrameId = null;

    // Store original value
    this.originalText = this.element.textContent;

    // Initialize
    this.setupObserver();
  }

  /**
   * Extract suffix from number string (e.g., "200 Mio.+" returns " Mio.+")
   */
  extractSuffix(text) {
    const match = text.match(/[\d.,\s]+(.*)$/);
    return match ? match[1] : '';
  }

  /**
   * Parse number from text, handling various formats
   */
  parseNumber(text) {
    // Remove all non-numeric characters except decimal separators
    let cleanText = text.replace(/[^\d.,]/g, '');

    // Replace comma with dot for parsing
    cleanText = cleanText.replace(',', '.');

    // Remove all dots except the last one (thousands separator vs decimal)
    const parts = cleanText.split('.');
    if (parts.length > 1) {
      cleanText = parts.slice(0, -1).join('') + '.' + parts[parts.length - 1];
    }

    const value = parseFloat(cleanText);
    return isNaN(value) ? 0 : value;
  }

  /**
   * Determine decimal places from original text
   */
  getDecimalPlaces(text) {
    const match = text.match(/[.,](\d+)/);
    return match ? match[1].length : 0;
  }

  /**
   * Easing function for smooth animation (exponential ease-out)
   */
  easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  /**
   * Format number with separators and decimals
   */
  formatNumber(value) {
    const fixed = value.toFixed(this.decimalPlaces);
    const parts = fixed.split('.');

    // Add thousands separator if specified
    if (this.thousandsSeparator) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
    }

    // Join with decimal separator
    let result = parts.join(this.separator);

    // Add suffix
    result += this.suffix;

    return result;
  }

  /**
   * Setup Intersection Observer for reliable scroll detection
   */
  setupObserver() {
    // Use IntersectionObserver for reliable viewport detection
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.3 // Trigger when 30% of element is visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.startAnimation();
        }
      });
    }, options);

    this.observer.observe(this.element);
  }

  /**
   * Start the counting animation
   */
  startAnimation() {
    this.hasAnimated = true;

    const startTime = performance.now();
    const startValue = this.startValue;
    const endValue = this.targetValue;
    const duration = this.duration;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing
      const easedProgress = this.easingFn(progress);

      // Calculate current value
      this.currentValue = startValue + (endValue - startValue) * easedProgress;

      // Update element
      this.element.textContent = this.formatNumber(this.currentValue);

      // Continue animation if not complete
      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact
        this.element.textContent = this.formatNumber(endValue);
        this.cleanup();
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Clean up resources
   */
  cleanup() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Reset the counter (useful for testing)
   */
  reset() {
    this.cleanup();
    this.hasAnimated = false;
    this.currentValue = this.startValue;
    this.element.textContent = this.originalText;
    this.setupObserver();
  }
}

/**
 * Initialize all counters on the page
 */
function initCounters(selector = '.counter', options = {}) {
  const counters = document.querySelectorAll(selector);
  const counterInstances = [];

  counters.forEach(element => {
    // Skip if already initialized
    if (element.hasAttribute('data-counter-initialized')) {
      return;
    }

    // Mark as initialized
    element.setAttribute('data-counter-initialized', 'true');

    // Create counter instance with custom options per element
    const customOptions = {
      ...options,
      duration: parseInt(element.getAttribute('data-duration')) || options.duration || 2000,
      startValue: parseFloat(element.getAttribute('data-start')) || options.startValue || 0,
      separator: element.getAttribute('data-separator') || options.separator || '.',
      thousandsSeparator: element.getAttribute('data-thousands-separator') || options.thousandsSeparator || ''
    };

    const counter = new CounterAnimation(element, customOptions);
    counterInstances.push(counter);
  });

  return counterInstances;
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CounterAnimation, initCounters };
}

// Also expose globally for jQuery compatibility
if (typeof window !== 'undefined') {
  window.CounterAnimation = CounterAnimation;
  window.initCounters = initCounters;
}
